import React, { ReactNode, RefObject, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import PubSub from 'pubsub-js';

import { useCommunityContext } from 'components/CommunityContext';
import TopBar from 'components/TopBar';
import MobileNav from 'components/MobileNav';
import ProfilePanel from 'components/Profile/ProfilePanel';
import { eventNames } from 'constants/eventNames';
import EventPanel from 'components/Events/components/EventPanel/EventPanel';
import useEventPanel from 'hooks/Events/useEventPanel';
import { setGlobalCSSVariables } from 'helpers/styleHelpers';
import ModeratorPanel from 'components/ModeratorPanel';
import ReplyPanel from 'components/ReplyPanel/ReplyPanel';
import { ChalkThemeProvider } from '@thinkific/chalk/themes';
import { Post } from '__generated__/graphql/legacy/graphql';
import { ReplyEditorProvider } from 'components/ReplyEditorContext/ReplyEditorContext';

interface PubSubInfo {
  userId: string;
  lastElementRef: RefObject<HTMLElement | null>;
  post?: Post;
  commentIdFromUrl?: string;
}

const MAP_FONTS_TO_CHALK = {
  'Source Sans Pro': 'sourceSans',
  'Droid Serif': 'notoSerif',
  Lato: 'lato',
  Montserrat: 'montserrat',
  'Open Sans': 'openSans',
  Raleway: 'raleway',
};

const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState<boolean>(false);
  const [isModeratorPanelOpen, setIsModeratorPanelOpen] = useState<boolean>(
    false
  );
  const [isReplyPanelOpen, setIsReplyPanelOpen] = useState<boolean>(false);
  const [lastFocusedElementRef, setLastFocusedElementRef] = useState<
    RefObject<HTMLElement | null>
  >();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedPost, setSelectedPost] = useState<Post | undefined>();
  const { community, site } = useCommunityContext();
  const {
    eventId,
    handleCloseEventPanel,
    isOpen: isEventPanelOpen,
  } = useEventPanel(() => setIsProfilePanelOpen(false));
  const { t } = useTranslation();

  const router = useRouter();
  const isPostPage = router.pathname === '/[communityId]/post/[postId]';
  const isEventPage = router.pathname === '/[communityId]/events/[eventId]';
  const isSearchPage = router.pathname === '/[communityId]/search';

  useEffect(() => {
    setGlobalCSSVariables(community.primaryColor);
  }, [community]);

  useEffect(() => {
    const profileToken = PubSub.subscribe(
      eventNames.OPEN_PROFILE_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenProfilePanel(info);
      }
    );

    const moderatorToken = PubSub.subscribe(
      eventNames.OPEN_MODERATOR_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenModeratorPanel(info);
      }
    );

    const replyToken = PubSub.subscribe(
      eventNames.OPEN_REPLY_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenReplyPanel(info);
      }
    );

    return () => {
      PubSub.unsubscribe(profileToken);
      PubSub.unsubscribe(moderatorToken);
      PubSub.unsubscribe(replyToken);
    };
  }, []);

  const handleCloseProfilePanel = () => {
    lastFocusedElementRef?.current?.focus();
    setSelectedUserId('');
    setIsProfilePanelOpen(false);
  };

  const handleOpenProfilePanel = (info: PubSubInfo) => {
    handleCloseEventPanel();
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedUserId(info.userId);
    setIsProfilePanelOpen(true);
  };

  const handleOpenModeratorPanel = (info: PubSubInfo) => {
    setLastFocusedElementRef(info.lastElementRef);
    setIsModeratorPanelOpen(true);
  };

  const handleCloseModeratorPanel = () => {
    lastFocusedElementRef?.current?.focus();
    setIsModeratorPanelOpen(false);
  };

  const handleOpenReplyPanel = (info: PubSubInfo) => {
    document.body.style.overflow = 'hidden';
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedPost(info?.post);
    setIsReplyPanelOpen(true);
  };

  const handleCloseReplyPanel = () => {
    document.body.style.overflow = 'visible';
    lastFocusedElementRef?.current?.focus();
    setIsReplyPanelOpen(false);
  };

  return (
    <>
      <Head>
        <title>{community.name}</title>
        <meta content={community.name} name="description" />
        <link href={site.favicon} rel="shortcut icon" type="image/x-icon" />
      </Head>
      <ChalkThemeProvider
        bodyFont={MAP_FONTS_TO_CHALK[community.font]}
        primaryColor={community.primaryColor}
        titleFont={MAP_FONTS_TO_CHALK[community.font]}
      >
        <TopBar
          isDashboardLinkVisible={!isPostPage && !isEventPage && !isSearchPage}
          t={t}
        />

        <MobileNav community={community} isPostView={isPostPage} />
        {children}
        <ProfilePanel
          isOpen={isProfilePanelOpen}
          userId={selectedUserId}
          onClose={handleCloseProfilePanel}
        />
        <EventPanel
          eventId={eventId}
          isOpen={isEventPanelOpen}
          onClose={handleCloseEventPanel}
        />
        <ModeratorPanel
          isOpen={isModeratorPanelOpen}
          onClose={handleCloseModeratorPanel}
        />
        <ReplyEditorProvider>
          <ReplyPanel
            isOpen={isReplyPanelOpen}
            postId={selectedPost?.id}
            onClose={handleCloseReplyPanel}
          />
        </ReplyEditorProvider>
      </ChalkThemeProvider>
    </>
  );
};

export const getLayout = (page: ReactNode): ReactNode => (
  <SiteLayout>{page}</SiteLayout>
);

export default SiteLayout;
